// Keep cart enhancements in sync after Shopify AJAX quantity/remove morphs.
(() => {
  const initialized = new WeakSet();
  const init = () => {
    const main = document.querySelector('#MainContent');
    if (!main || !document.documentElement.classList.contains('hn-global-cart-context') || initialized.has(main)) return;
    initialized.add(main);
    const sync = () => {
      if (document.documentElement.classList.contains('hn-global-cart-context')) {
        const heading = [...main.querySelectorAll('h2, h3')].find((item) => item.textContent.trim() === 'You may also like');
        if (heading) heading.textContent = 'Related items you may like';
      }
    };
    sync();
    new MutationObserver(sync).observe(main, { childList: true, subtree: true });

    const finalizeCartEdit = async () => {
      let reloading = false;
      try {
        if (await window.HanossFinalizeCartEdit()) {
          reloading = true;
          location.reload();
        }
      } catch { /* Preserve pending replacement and both items for recovery. */ }
      finally { if (!reloading) document.documentElement.classList.remove('hn-cart-edit-finalizing'); }
    };
    finalizeCartEdit();

    const finalizeAddNew = async () => {
      let pending;
      try { pending = JSON.parse(sessionStorage.getItem('hn-hng04-cart-add-new-restore') || 'null'); } catch { return; }
      if (!pending?.original || pending.expires < Date.now() || !Array.isArray(pending.before)) {
        sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
        return;
      }
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) return;
        const cart = await response.json();
        const before = new Map(pending.before.map((item) => [item.key, item.quantity]));
        const added = cart.items.some((item) => item.product_id === pending.original.productId
          && item.quantity > (before.get(item.key) || 0));
        if (!added) {
          sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
          return;
        }
        const originalId = pending.original.properties?.['_customily-personalization-id'];
        const originalStillPresent = cart.items.some((item) => item.key === pending.original.key
          || (originalId && item.properties?.['_customily-personalization-id'] === originalId));
        if (originalStillPresent) {
          sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
          return;
        }
        const properties = Object.fromEntries(Object.entries(pending.original.properties || {}).filter(([, value]) => value != null));
        const restore = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: pending.original.variantId, quantity: pending.original.quantity, properties }),
        });
        if (!restore.ok) return;
        sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
        location.reload();
      } catch { /* Keep the saved original available for a retry on the next cart load. */ }
    };
    finalizeAddNew();

    // Region dialog lifecycle is owned by hn-ui-header-search.js on every page.

    let pendingRemove = null;
    let confirmedRemove = null;
    const confirm = document.createElement('dialog');
    confirm.className = 'hn-ui-cart-confirm';
    confirm.setAttribute('aria-labelledby', 'hn-ui-cart-confirm-title');
    confirm.innerHTML = `
      <button class="hn-ui-cart-confirm__close" type="button" aria-label="Close remove confirmation">×</button>
      <h2 id="hn-ui-cart-confirm-title">Are you sure you want to remove this item?</h2>
      <img class="hn-ui-cart-confirm__image" alt="" width="468" height="468">
      <div class="hn-ui-cart-confirm__actions">
        <button class="hn-ui-cart-confirm__remove" type="button">Remove</button>
        <button class="hn-ui-cart-confirm__keep" type="button">Keep</button>
      </div>
    `;
    document.body.append(confirm);

    const closeConfirm = () => confirm.close();
    confirm.querySelector('.hn-ui-cart-confirm__close').addEventListener('click', closeConfirm);
    confirm.querySelector('.hn-ui-cart-confirm__keep').addEventListener('click', closeConfirm);
    confirm.querySelector('.hn-ui-cart-confirm__remove').addEventListener('click', () => {
      const button = pendingRemove;
      pendingRemove = null;
      confirm.close();
      if (!button?.isConnected) return;
      confirmedRemove = button;
      button.click();
    });
    confirm.addEventListener('click', (event) => {
      if (event.target === confirm) closeConfirm();
    });
    confirm.addEventListener('close', () => {
      document.body.classList.remove('hn-ui-cart-confirm-open');
      if (pendingRemove?.isConnected) pendingRemove.focus();
      pendingRemove = null;
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('.cart-page .cart-items__remove');
      if (!button || !main.contains(button)) return;
      if (button === confirmedRemove) {
        confirmedRemove = null;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const row = button.closest('.cart-items__table-row');
      const sourceImage = row?.querySelector('.cart-items__media-image');
      const preview = confirm.querySelector('.hn-ui-cart-confirm__image');
      preview.src = sourceImage?.currentSrc || sourceImage?.src || '';
      preview.alt = sourceImage?.alt || row?.querySelector('.cart-items__title')?.textContent?.trim() || 'Cart item';
      pendingRemove = button;
      document.body.classList.add('hn-ui-cart-confirm-open');
      confirm.showModal();
      confirm.querySelector('.hn-ui-cart-confirm__keep').focus();
    }, true);

    const imageDialog = document.createElement('dialog');
    imageDialog.className = 'hn-ui-cart-image-dialog';
    imageDialog.setAttribute('aria-label', 'Product image');
    imageDialog.setAttribute('tabindex', '-1');
    imageDialog.innerHTML = `
      <button class="hn-ui-cart-image-dialog__close" type="button" aria-label="Close image preview">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
      </button>
      <img class="hn-ui-cart-image-dialog__image" alt="">
      <button class="hn-ui-cart-image-dialog__checkout" type="button">Proceed to Checkout</button>
    `;
    document.body.append(imageDialog);
    let imageTrigger = null;
    const closeImageDialog = (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      if (imageDialog.open) imageDialog.close();
    };
    const imageCloseButton = imageDialog.querySelector('.hn-ui-cart-image-dialog__close');
    // Safari can retarget the synthetic click when a modal closes on touchend.
    // Closing on pointerdown keeps the event on the control and prevents click-through.
    imageCloseButton.addEventListener('pointerdown', closeImageDialog);
    imageCloseButton.addEventListener('click', closeImageDialog);
    imageDialog.addEventListener('click', (event) => {
      if (event.target === imageDialog) closeImageDialog();
    });
    imageDialog.addEventListener('close', () => {
      document.body.classList.remove('hn-ui-cart-image-open');
      if (imageTrigger?.isConnected) imageTrigger.focus();
      imageTrigger = null;
    });
    imageDialog.querySelector('.hn-ui-cart-image-dialog__checkout').addEventListener('click', () => {
      main.querySelector('.cart-page .cart__checkout-button')?.click();
    });
    main.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('.hn-ui-cart-view');
      if (!trigger || !main.contains(trigger)) return;
      const sourceImage = trigger.closest('.cart-items__media')?.querySelector('.cart-items__media-image');
      const preview = imageDialog.querySelector('.hn-ui-cart-image-dialog__image');
      const fallbackSrc = trigger.dataset.previewFallback || sourceImage?.currentSrc || sourceImage?.src || '';
      preview.onerror = () => {
        preview.onerror = null;
        if (preview.src !== new URL(fallbackSrc, location.href).href) preview.src = fallbackSrc;
      };
      preview.src = trigger.dataset.previewSrc || fallbackSrc;
      preview.alt = sourceImage?.alt || 'Product image';
      imageTrigger = trigger;
      document.body.classList.add('hn-ui-cart-image-open');
      imageDialog.showModal();
      imageDialog.focus();
    });

    const editDialog = document.createElement('dialog');
    editDialog.className = 'hn-ui-cart-edit-dialog';
    editDialog.setAttribute('aria-labelledby', 'hn-ui-cart-edit-title');
    editDialog.innerHTML = `
      <div class="hn-ui-cart-edit-dialog__heading">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="m4 16.9-.9 4 4-.9L19.6 7.5a2.1 2.1 0 0 0-3-3L4 16.9Z"/></svg>
        <h2 id="hn-ui-cart-edit-title">Edit Item</h2>
      </div>
      <button class="hn-ui-cart-edit-dialog__close" type="button" aria-label="Close item editor">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
      </button>
      <div class="hn-ui-cart-edit-dialog__stage">
        <div class="hn-ui-cart-edit-dialog__loading" role="status">Loading item editor…</div>
        <div class="hn-ui-cart-edit-dialog__placeholder" aria-hidden="true"></div>
      </div>
    `;
    document.body.append(editDialog);
    let editTrigger = null;
    const closeEditDialog = () => editDialog.close();
    editDialog.querySelector('.hn-ui-cart-edit-dialog__close').addEventListener('click', closeEditDialog);
    editDialog.addEventListener('click', (event) => {
      if (event.target === editDialog) closeEditDialog();
    });
    editDialog.addEventListener('close', () => {
      sessionStorage.removeItem('hn-hng04-cart-edit-intent');
      sessionStorage.removeItem('hn-hng04-cart-legacy-replace');
      sessionStorage.removeItem('hn-hng04-cart-return');
      if (editTrigger?.isConnected) editTrigger.focus();
      editTrigger = null;
    });

    main.addEventListener('click', async (event) => {
      const trigger = event.target.closest?.('.hn-ui-cart-edit');
      if (!trigger || !main.contains(trigger)) return;
      let editUrl;
      let productUrl;
      try {
        editUrl = new URL(trigger.dataset.editUrl, location.href);
        productUrl = new URL(trigger.dataset.productUrl, location.href);
        const storeDomain = window.Shopify?.shop;
        const localPreview = ['127.0.0.1', 'localhost'].includes(location.hostname)
          && editUrl.hostname === location.hostname;
        if ((editUrl.hostname === storeDomain || localPreview) && editUrl.origin !== location.origin) {
          editUrl = new URL(editUrl.pathname + editUrl.search + editUrl.hash, location.origin);
        }
        if (editUrl.origin !== location.origin || editUrl.pathname !== productUrl.pathname) return;
      } catch { return; }
      try {
        sessionStorage.setItem('hn-hng04-cart-return', JSON.stringify({
          capturedAt: Date.now(),
          main: main.outerHTML,
          footer: document.querySelector('.page-wrapper > footer')?.outerHTML || '',
          title: document.title,
          bodyClass: document.body.className,
          htmlClass: document.documentElement.className,
          scrollY: window.scrollY,
          scripts: [...main.querySelectorAll('script[src]')].map((script) => ({
            src: script.src,
            type: script.type,
          })),
          cartScript: [...document.scripts].find((script) => script.src.includes('/hn-ui-cart.js'))?.src || '',
        }));
      } catch { sessionStorage.removeItem('hn-hng04-cart-return'); }
      editTrigger = trigger;
      editDialog.querySelector('.hn-ui-cart-edit-dialog__loading').textContent = 'Loading item editor…';
      editDialog.showModal();
      try {
        const background = main.querySelector('.cart-page')?.cloneNode(true);
        background?.querySelectorAll('script, iframe, form, input, button, [class*="customily"], [id*="customily"]').forEach((node) => node.remove());
        if (background) sessionStorage.setItem('hn-hng04-cart-edit-background', background.outerHTML);
      } catch {}
      // Prefer Customily's stored design when it belongs to this exact product.
      // The editor checks its hydrated fields against the cart before enabling Save.
      let beforeCart;
      let freshUrl;
      trigger.disabled = true;
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Cart unavailable');
        beforeCart = await response.json();
        if (!beforeCart.items.some((item) => item.key === trigger.dataset.editLineKey)) throw new Error('Item unavailable');
        const savedReturn = JSON.parse(sessionStorage.getItem('hn-hng04-cart-return') || 'null');
        if (savedReturn) {
          savedReturn.cartSignature = JSON.stringify(beforeCart.items.map((item) => [item.key, item.quantity]));
          sessionStorage.setItem('hn-hng04-cart-return', JSON.stringify(savedReturn));
        }
      } catch {
        trigger.disabled = false;
        editDialog.close();
        return;
      }
      try {
        const original = beforeCart.items.find((item) => item.key === trigger.dataset.editLineKey);
        const previewImage = original.properties?.['_customily-preview']
          || original.properties?.['_customily-thumb']
          || original.properties?.['_personalization_preview']
          || '';
        const cleanUrl = new URL(productUrl.pathname, location.origin);
        cleanUrl.searchParams.set('variant', String(original.variant_id));
        const storedUrl = new URL(editUrl.pathname + editUrl.search, location.origin);
        const useStored = storedUrl.searchParams.get('isStoredCustomization') === '1'
          && Boolean(storedUrl.searchParams.get('customizationId'))
          && Boolean(storedUrl.searchParams.get('templateId'))
          && storedUrl.searchParams.get('variant') === String(original.variant_id);
        freshUrl = useStored ? storedUrl : cleanUrl;
        sessionStorage.setItem('hn-hng04-cart-edit-intent', JSON.stringify({
          path: freshUrl.pathname,
          search: freshUrl.search,
          lineKey: trigger.dataset.editLineKey,
          originalQuantity: original.quantity,
          originalVariantId: original.variant_id,
          properties: original.properties && typeof original.properties === 'object' ? original.properties : {},
          previewImage,
          expires: Date.now() + 60 * 60 * 1000,
          legacy: !useStored,
          fallbackUrl: cleanUrl.href,
        }));
      } catch {
        trigger.disabled = false;
        editDialog.close();
        return;
      }
      location.assign(freshUrl.href);
    });
  };
  window.HanossInitCart = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
